import { ApiError } from "../api.ts";
import { clear, errorBox, h, link } from "../dom.ts";
import { navigate, setTitle } from "../router.ts";
import { currentMe, nameOf, signedIn, signOut, startSignIn, tokenFromInput, verifySignIn, type Notice } from "../session.ts";
import { accountStyles, artWall, needsFirstRun } from "./me-kit.ts";
import { appHandoffLink, conditionalSupported, finishProvider, getMethods, isChallenge, passkeySignIn, passkeysSupported, PROVIDER_NAME, startProvider, type Provider } from "./me-methods.ts";

/** Only same-site paths are allowed after sign-in. */
export function safeNext(next: string | null): string {
  return next && next.startsWith("/") && !next.startsWith("//") ? next : "/";
}

/** What a magic-link landing ended in, from accounts' error code and what this browser knows. */
export type LandingState = "used" | "expired" | "invalid" | "signed_in" | "bad_link" | "rate_limited" | "error";

/**
 * accounts (wave 6) answers `401 link_expired` and `401 link_used`, and `401 invalid_token` only for a link it doesn't
 * know. Older builds sent `invalid_token` for all three, so a link this browser already used still reads as used.
 */
export function landingState(code: string | null, ctx: { usedHere: boolean; signedIn: boolean }): LandingState {
  const used = code === "link_used" || (code === "invalid_token" && ctx.usedHere);
  if (used || code === "invalid_token" || code === "link_expired") {
    if (ctx.signedIn) return "signed_in";
    if (used) return "used";
    if (code === "link_expired") return "expired";
    return "invalid";
  }
  if (code === "rate_limited") return "rate_limited";
  if (code === "bad_link") return "bad_link";
  return "error";
}

/** The app's own link for a sign-in token (accounts' MAGIC_LINK_BASE); the launcher signs in with it. */
export function appLink(token: string): string {
  return `thrdm://auth/email?token=${encodeURIComponent(token)}`;
}

/** Where to go once signed in: first run for accounts that haven't seen it, else where they were heading. */
export function afterSignIn(next: string, me: { onboardedAt?: number | null } | null): string {
  // the Banger app asked for this sign-in (any method, any tab): hand it over first; first run happens in the app
  const app = pendingApp();
  if (app) return `/signin?app=1&cc=${app}`;
  return needsFirstRun(me) ? `/welcome${next !== "/" ? `?next=${encodeURIComponent(next)}` : ""}` : next;
}

// ---- the app's sign-in (wave 12): the launcher opens /signin?app=1&cc=<its PKCE challenge> ----------------------
// The challenge is kept for 15 minutes in this browser, so whichever way the person signs in (an email link opened
// in a new tab, Discord, a passkey) ends at the handoff.

const APP_KEY = "thrdm.appSignin";

export function rememberApp(cc: string): void {
  try {
    localStorage.setItem(APP_KEY, JSON.stringify({ cc, at: Date.now() }));
  } catch {
    /* without storage the handoff still works in this tab */
  }
}

export function pendingApp(now = Date.now()): string | null {
  try {
    const v = JSON.parse(localStorage.getItem(APP_KEY) ?? "null") as { cc?: string; at?: number } | null;
    return v && isChallenge(v.cc ?? null) && now - (v.at ?? 0) < 15 * 60_000 ? v.cc! : null;
  } catch {
    return null;
  }
}

const forgetApp = () => {
  try {
    localStorage.removeItem(APP_KEY);
  } catch {
    /* nothing kept */
  }
};

/** Signed in, and the app asked: one button hands this sign-in to it (a one-time code only the app can use). */
function appPage(cc: string): HTMLElement {
  const me = currentMe();
  const err = h("div", { "aria-live": "polite" });
  const open = h("button", { class: "btn wide", type: "button" }, "Open Banger");
  const done = h("p", { class: "fine", hidden: true }, "Banger should be signed in now. You can close this tab.");
  open.addEventListener("click", async () => {
    open.setAttribute("disabled", "");
    clear(err);
    try {
      const href = await appHandoffLink(cc);
      forgetApp();
      location.href = href;
      done.hidden = false;
      open.textContent = "Open Banger again";
    } catch (e) {
      clear(err, errorBox(e));
    } finally {
      open.removeAttribute("disabled");
    }
  });
  const other = h("button", { class: "acct-link", type: "button" }, "Use another account");
  other.addEventListener("click", async () => {
    await signOut();
    rememberApp(cc);
  });
  return stage(
    card(
      state(
        "↗",
        "go",
        "Sign in to the Banger app",
        h("p", null, me ? `You're signed in here as ${nameOf(me)}. Open Banger to use this account in the app.` : "Open Banger to finish."),
        h("div", { class: "acct-row" }, open),
        done,
        err,
        h("p", { class: "fine" }, "Not you? ", other, "."),
      ),
    ),
    { title: "Almost there.", lede: "Your account, in the app too: library, follows and friends.", perks: false },
  );
}

const USED_KEY = "thrdm.usedLinks";

/** A short hash of a link token, so the browser can recognise a link it already used without keeping the token. */
async function linkHash(token: string): Promise<string> {
  const d = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(token));
  return [...new Uint8Array(d).slice(0, 12)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

function usedLinks(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(USED_KEY) ?? "[]");
    return Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function rememberUsed(hash: string): void {
  try {
    localStorage.setItem(USED_KEY, JSON.stringify([hash, ...usedLinks().filter((x) => x !== hash)].slice(0, 20)));
  } catch {
    /* storage off: we just can't say "already used" later */
  }
}

// ---- the stage -------------------------------------------------------------------------------------------------

/** The sign-in stage: drifting art behind, the promise on the left, the card on the right. */
function stage(card: HTMLElement, opts: { title?: string; lede?: string; perks?: boolean } = {}): HTMLElement {
  accountStyles();
  return h(
    "div",
    { class: "acct" },
    artWall(),
    h(
      "div",
      { class: "acct-signin" },
      h(
        "div",
        { class: "acct-signin-in" },
        h(
          "div",
          { class: "acct-hero" },
          h("p", { class: "kicker" }, "Banger"),
          h("h1", null, ...(opts.title ? [opts.title] : ["Walk in. ", h("span", { class: "hot" }, "Your games are waiting.")])),
          h("p", { class: "lede" }, opts.lede ?? "One account for the website and the Banger app. Your library, the games you love and the creators you follow come with you."),
          opts.perks === false
            ? null
            : h(
                "ul",
                { class: "acct-perks" },
                h("li", null, h("span", null, h("b", null, "No password. "), "We email you a link; open it and you're in.")),
                h("li", null, h("span", null, h("b", null, "Pick up where you left off "), "on any device.")),
                h("li", null, h("span", null, h("b", null, "Follow creators "), "and hear when they ship something new.")),
              ),
        ),
        card,
      ),
    ),
  );
}

function card(...children: (HTMLElement | null | (HTMLElement | null)[])[]): HTMLElement {
  return h("div", { class: "acct-card acct-enter", "aria-live": "polite" }, ...children);
}

function state(glyph: string, cls: string, title: string, ...body: (HTMLElement | string | null | HTMLElement[])[]): HTMLElement {
  return h("div", { class: "acct-state acct-enter" }, h("div", { class: `acct-glyph ${cls}`, "aria-hidden": "true" }, glyph), h("div", null, h("h2", null, title), ...body));
}

const newLinkHref = (next: string) => `/signin${next !== "/" ? `?next=${encodeURIComponent(next)}` : ""}`;

// ---- landing from a link ---------------------------------------------------------------------------------------

/**
 * The landing from a web magic link (/signin?token=ml_…). The token leaves the address bar at once, and one
 * verify runs per token even when the page re-renders (signing in re-renders every page).
 */
let landing: { token: string; next: string; el: HTMLElement; done: boolean } | null = null;

function landingPage(token: string, next: string, forApp: boolean): HTMLElement {
  if (landing?.token === token) return landing.el;
  const box = card(state("✦", "go", "Opening the door…", h("p", { class: "dim" }, "Signing you in.")));
  const el = stage(box, { title: "Welcome back.", lede: "One moment while we let you in.", perks: false });
  const me: { token: string; next: string; el: HTMLElement; done: boolean } = { token, next, el, done: false };
  landing = me;
  const valid = tokenFromInput(token);
  if (forApp && valid) {
    // A link the app asked for: hand the token to the app. Only one side may use it, so the web waits for a choice.
    me.done = true;
    clear(box, appHandoff(valid, () => void verify(valid)));
    return el;
  }
  void verify(valid).then(() => (me.done = true));
  async function verify(t: string | null): Promise<void> {
    clear(box, state("✦", "go", "Opening the door…", h("p", { class: "dim" }, "Signing you in.")));
    const hash = t ? await linkHash(t).catch(() => null) : null;
    if (!t) return show(box, "bad_link", next);
    try {
      const v = await verifySignIn(t);
      if (hash) rememberUsed(hash);
      const to = afterSignIn(next, v.user as { onboardedAt?: number | null });
      if (v.created && v.notices.length && !to.startsWith("/welcome")) {
        // a new account on an older accounts build (no first run): the sign-up notices must be shown before moving on
        clear(
          box,
          state("✓", "go", "Welcome to Banger", h("p", null, "Your account is ready and you're signed in."), notices(v.notices), link(next, { class: "btn" }, "Continue")),
        );
        return;
      }
      clear(box, state("✓", "go", "You're in", h("p", { class: "dim" }, "Taking you there now…"), link(to, { class: "btn" }, "Continue")));
      navigate(to, true);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : null;
      const s = landingState(code, { usedHere: !!hash && usedLinks().includes(hash), signedIn: signedIn() });
      show(box, s, next, e);
    }
  }
  return el;
}

/** The launcher's link opened in a browser: open the app with it, or sign in here instead. */
function appHandoff(token: string, here: () => void): HTMLElement {
  const href = appLink(token);
  const open = h("a", { class: "btn wide", href }, "Open Banger");
  // try the app once; if it isn't installed the browser just stays here
  setTimeout(() => {
    try {
      location.href = href;
    } catch {
      /* blocked: the button still works */
    }
  }, 350);
  return state(
    "↗",
    "go",
    "Opening the Banger app",
    h("p", null, "You asked to sign in from the app. If it didn't open, use the button."),
    h("div", { class: "acct-row" }, open),
    h("p", { class: "fine" }, "No app on this device? ", h("button", { class: "acct-link", type: "button", onclick: here }, "Sign in on this browser instead"), ". The link works once, so it's one or the other."),
  );
}

function notices(list: Notice[]): HTMLElement[] {
  return list.map((n) => h("div", { class: "note", role: "note" }, h("strong", null, n.title), h("p", { class: "small", style: { margin: "4px 0 0" } }, n.message)));
}

function show(box: HTMLElement, s: LandingState, next: string, e?: unknown): void {
  const again = h("div", { class: "acct-row" }, link(newLinkHref(next), { class: "btn" }, "Send me a new link"));
  const why = h("p", { class: "fine" }, "Sign-in links work once and expire 15 minutes after we send them.");
  const views: Record<LandingState, () => HTMLElement> = {
    signed_in: () => state("✓", "go", "You're already signed in", h("p", null, "That link can't be used again, but this browser is signed in."), h("div", { class: "acct-row" }, link(next, { class: "btn" }, "Continue"))),
    used: () => state("↺", "", "This link was already used", h("p", null, "Each link opens the door once. Ask for a fresh one and you're back in."), again, why),
    expired: () => state("⌛", "", "This link has expired", h("p", null, "Links last 15 minutes. Ask for a new one and open it right away."), again, why),
    invalid: () => state("?", "", "This link doesn't work", h("p", null, "We don't recognise it. Copy the whole link from the latest email, or ask for a new one; it arrives in a minute."), again, why),
    bad_link: () => state("✂", "", "That link looks cut off", h("p", null, "Copy the whole link from the email, or ask for a new one."), again),
    rate_limited: () => state("…", "", "Too many tries", h("p", null, "Give it a few minutes, then open the link again."), h("div", { class: "acct-row" }, link("/signin", { class: "btn ghost" }, "Back to sign in"))),
    error: () => state("!", "", "We couldn't sign you in", e ? errorBox(e) : null, h("p", { class: "fine" }, "Open the link from the email again, or ask for a new one."), again),
  };
  clear(box, views[s]());
}

// ---- the age question (new accounts) ---------------------------------------------------------------------------
// One plain question, no date and no hint of the cutoff beside it; the backend keeps only the age group.

export const AGE_LABEL = "How old are you?";
export const AGE_HINT = "Type your age as a number.";
export const AGE_FINE = "We keep only your age group.";

/** The typed age as whole years, or null when it isn't just digits. */
export function readAge(v: string): number | null {
  const t = v.trim();
  return /^[0-9]{1,3}$/.test(t) ? parseInt(t, 10) : null;
}

/** The age input: a short numeric text field (a phone shows the number pad); Enter submits its form. */
function ageField(id: string): HTMLInputElement {
  return h("input", { id, name: "age", type: "text", inputmode: "numeric", pattern: "[0-9]*", maxlength: "3", autocomplete: "off" }) as HTMLInputElement;
}

// ---- back from Discord or GitHub -------------------------------------------------------------------------------

/** Why a provider round trip came back without a sign-in (`?oauth_error=` from accounts' callback). */
export const OAUTH_ERRORS: Record<string, string> = {
  denied: "You cancelled at the provider. Pick another way to sign in, or try again.",
  expired: "That sign-in took too long or was already used. Please try again.",
  unavailable: "The provider didn't answer. Try again in a moment, or sign in with email.",
  failed: "That sign-in didn't work. Please try again, or sign in with email.",
};

let finishing: { code: string; el: HTMLElement } | null = null;

function oauthLanding(code: string, next: string): HTMLElement {
  if (finishing?.code === code) return finishing.el;
  const box = card(state("✦", "go", "Opening the door…", h("p", { class: "dim" }, "Finishing your sign-in.")));
  const el = stage(box, { title: "Welcome.", lede: "One moment while we let you in.", perks: false });
  finishing = { code, el };
  const run = async (age?: number) => {
    try {
      const r = await finishProvider(code, age);
      if (r.kind === "linked") return navigate("/me/settings#signin-methods", true);
      const to = afterSignIn(r.next ? safeNext(r.next) : next, r.user as { onboardedAt?: number | null });
      clear(box, state("✓", "go", "You're in", h("p", { class: "dim" }, "Taking you there now…")));
      finishing = null;
      navigate(to, true);
    } catch (e) {
      const c = e instanceof ApiError ? e.code : (e as { code?: string }).code;
      // "birth_date_required" is the backend's name for "a new account: ask for the age"
      if (c === "birth_date_required") return askAge();
      // a mistyped age keeps the sign-in code, so the same screen can try again
      if (c === "invalid_age") return askAge(AGE_HINT);
      const again = h("div", { class: "acct-row" }, link(newLinkHref(next), { class: "btn" }, "Back to sign in"));
      const words: Record<string, [string, string]> = {
        under_age: ["Banger is for people 13 and older", "We haven't kept anything about you."],
        no_verified_email: ["We need a verified email", e instanceof Error ? e.message : ""],
        code_invalid: ["This sign-in has expired", "It took too long, was already used, or started in another tab. Please try again."],
      };
      const [t, d] = words[c ?? ""] ?? ["We couldn't sign you in", ""];
      clear(box, state("!", "", t, d ? h("p", null, d) : errorBox(e), again));
    }
  };
  const askAge = (problem?: string) => {
    const ageInput = ageField("oauth-age");
    const aerr = h("div", { "aria-live": "polite" }, problem ? h("p", { class: "error", role: "alert" }, problem) : null);
    const go = h("button", { class: "btn wide", type: "submit" }, "Create my account");
    clear(
      box,
      h(
        "form",
        {
          class: "stack acct-enter",
          novalidate: true,
          onsubmit: (e: Event) => {
            e.preventDefault();
            const age = readAge(ageInput.value);
            if (age === null) {
              clear(aerr, h("p", { class: "error", role: "alert" }, AGE_HINT));
              ageInput.focus();
              return;
            }
            go.setAttribute("disabled", "");
            void run(age);
          },
        },
        h("h2", null, "New here? Welcome."),
        h("p", null, "One thing before we make your account."),
        h("div", { class: "field" }, h("label", { for: "oauth-age" }, AGE_LABEL), ageInput),
        aerr,
        go,
        h("p", { class: "fine" }, AGE_FINE),
      ),
    );
    ageInput.focus();
  };
  void run();
  return el;
}

// ---- the form --------------------------------------------------------------------------------------------------

export function signinPage(_p: unknown, query: URLSearchParams): HTMLElement {
  setTitle("Sign in");
  const next = safeNext(query.get("next"));

  // A web magic link (/signin?token=ml_…) signs in directly; the token leaves the address bar (and history) at once.
  const token = query.get("token");
  if (token !== null) {
    history.replaceState(null, "", newLinkHref(next));
    return landingPage(token, next, query.get("app") === "1");
  }
  // the re-render that signing in triggers while the landing is still at work (the token is gone from the address)
  if (landing && !landing.done) return landing.el;
  // the handoff screen stays up when the page re-renders (it never signed this browser in)
  if (landing && landing.done && !signedIn() && landing.el.isConnected) return landing.el;
  landing = null;

  // the Banger app asked (cc = its PKCE challenge): remember it for whichever way the person signs in
  const cc = query.get("app") === "1" ? query.get("cc") : null;
  if (isChallenge(cc)) rememberApp(cc);
  if (signedIn() && pendingApp()) return appPage(pendingApp()!);

  // back from Discord or GitHub
  const oauth = query.get("oauth");
  if (oauth) {
    history.replaceState(null, "", newLinkHref(next));
    return oauthLanding(oauth, next);
  }

  const box = card();
  if (signedIn()) {
    clear(box, state("✓", "go", "You're signed in", h("p", { class: "dim" }, "This browser already knows you."), h("div", { class: "acct-row" }, link(next, { class: "btn" }, "Continue"), link("/me", { class: "btn ghost" }, "Your space"))));
    return stage(box);
  }

  const err = h("div", { "aria-live": "polite" });
  const ageInput = ageField("age");
  const ageBox = h("div", { class: "field", hidden: true }, h("label", { for: "age" }, AGE_LABEL), ageInput, h("p", { class: "fine" }, AGE_FINE));
  // "username webauthn": the browser offers saved passkeys right in the email field (conditional UI)
  const email = h("input", { id: "email", name: "email", type: "email", required: true, autocomplete: "username webauthn", placeholder: "you@example.com", autofocus: true });
  // one WebAuthn request at a time: the autofill one is aborted before any other sign-in starts
  let autofill: AbortController | null = null;
  const stopAutofill = () => {
    autofill?.abort();
    autofill = null;
  };
  const signedInBy = (v: { user: { onboardedAt?: number | null } } | null) => {
    if (v) navigate(afterSignIn(next, v.user), true);
  };
  const others = h("div", { class: "acct-methods" });
  void getMethods().then(async (m) => {
    const providers = (["discord", "github"] as Provider[]).filter((p) => m[p]);
    const passkey = m.passkey && passkeysSupported();
    if (!providers.length && !passkey) return;
    clear(
      others,
      providers.map((p) =>
        h(
          "button",
          {
            class: `btn wide ghost acct-provider ${p}`,
            type: "button",
            onclick: async (e: Event) => {
              stopAutofill();
              const b = e.currentTarget as HTMLButtonElement;
              b.setAttribute("disabled", "");
              clear(err);
              try {
                await startProvider(p, { next });
              } catch (x) {
                b.removeAttribute("disabled");
                clear(err, errorBox(x));
              }
            },
          },
          `Continue with ${PROVIDER_NAME[p]}`,
        ),
      ),
      passkey
        ? h(
            "button",
            {
              class: "btn wide ghost acct-provider passkey",
              type: "button",
              onclick: async () => {
                stopAutofill();
                clear(err);
                try {
                  signedInBy(await passkeySignIn());
                } catch (x) {
                  clear(err, errorBox(x));
                }
              },
            },
            h("span", { class: "acct-provider-mark", "aria-hidden": "true" }, "⚿"),
            "Sign in with a passkey",
          )
        : null,
      h("div", { class: "acct-or" }, h("span", null, "or with email")),
    );
    if (passkey && (await conditionalSupported())) {
      autofill = new AbortController();
      void passkeySignIn({ conditional: true, signal: autofill.signal }).then(signedInBy, (x) => clear(err, errorBox(x)));
    }
  });
  const oauthError = query.get("oauth_error");
  if (oauthError) {
    clear(err, h("p", { class: "error", role: "alert" }, OAUTH_ERRORS[oauthError] ?? OAUTH_ERRORS.failed!));
    history.replaceState(null, "", newLinkHref(next));
  }
  const submit = h("button", { class: "btn wide", type: "submit" }, "Email me a sign-in link");
  const form = h(
    "form",
    {
      class: "stack",
      // the browser checks the email; the age gets the short inline note below instead of a validation bubble
      novalidate: true,
      onsubmit: async (e: Event) => {
        e.preventDefault();
        if (!email.checkValidity()) return void email.reportValidity();
        stopAutofill();
        clear(err);
        const address = email.value.trim();
        let age: number | undefined;
        if (!ageBox.hidden) {
          const a = readAge(ageInput.value);
          if (a === null) {
            clear(err, h("p", { class: "error", role: "alert" }, AGE_HINT));
            ageInput.focus();
            return;
          }
          age = a;
        }
        submit.setAttribute("disabled", "");
        submit.textContent = "Sending…";
        try {
          const r = await startSignIn(address, age);
          sent(address, age, r.dev?.token ?? null, r.newAccount, r.notices ?? []);
        } catch (x) {
          // "birth_date_required" is the backend's name for "a new account: ask for the age"
          if (x instanceof ApiError && x.code === "birth_date_required") {
            ageBox.hidden = false;
            ageInput.focus();
            clear(err, h("p", { class: "note" }, "New here? Welcome. Tell us your age to create your account."));
          } else if (x instanceof ApiError && x.code === "invalid_age") {
            clear(err, h("p", { class: "error", role: "alert" }, AGE_HINT));
            ageInput.focus();
          } else if (x instanceof ApiError && x.code === "under_age") {
            clear(err, h("p", { class: "error", role: "alert" }, "Sorry, Banger is for people 13 and older."));
          } else if (x instanceof ApiError && x.code === "email_quota") {
            // accounts' daily email budget is spent; its message says when sign-ins resume
            clear(err, h("p", { class: "error", role: "alert" }, "We can't send sign-in emails right now. ", x.message));
          } else if (x instanceof ApiError && x.code === "email_unavailable") {
            clear(err, h("p", { class: "error", role: "alert" }, "We can't send sign-in emails right now. Please try again in a little while."));
          } else clear(err, errorBox(x));
        } finally {
          submit.removeAttribute("disabled");
          submit.textContent = ageBox.hidden ? "Email me a sign-in link" : "Create my account";
        }
      },
    },
    h("h2", null, pendingApp() ? "Sign in to the Banger app" : "Sign in or join"),
    h("p", { class: "dim" }, pendingApp() ? "Sign in here, then we'll hand you back to the app." : "New or returning, it's the same door."),
    others,
    h("div", { class: "field" }, h("label", { for: "email" }, "Email"), email),
    ageBox,
    submit,
    err,
    h("p", { class: "fine" }, "By joining you agree to play fair and be kind; we'll show you the house rules once you're in."),
  );

  const sent = (address: string, age: number | undefined, devToken: string | null, isNew: boolean, list: Notice[]) => {
    const paste = h("input", { id: "paste", placeholder: "Paste the link from the email", autocomplete: "off" });
    const perr = h("div", { "aria-live": "polite" });
    const go = async (t: string | null) => {
      if (!t) return clear(perr, h("p", { class: "error" }, "That doesn't look like a sign-in link."));
      try {
        const v = await verifySignIn(t);
        navigate(afterSignIn(next, v.user as { onboardedAt?: number | null }), true);
      } catch (x) {
        const code = x instanceof ApiError ? x.code : null;
        if (code === "link_expired" || code === "link_used") show(box, code === "link_used" ? "used" : "expired", next);
        else clear(perr, errorBox(x));
      }
    };
    // resend after a short wait (accounts allows 5 per email per 15 minutes)
    let wait = 30;
    const resend = h("button", { class: "acct-link", type: "button", disabled: true }, `Send it again (${wait}s)`);
    const timer = setInterval(() => {
      wait--;
      if (!resend.isConnected) return clearInterval(timer);
      if (wait > 0) resend.textContent = `Send it again (${wait}s)`;
      else {
        clearInterval(timer);
        resend.removeAttribute("disabled");
        resend.textContent = "Send it again";
      }
    }, 1000);
    resend.addEventListener("click", async () => {
      resend.setAttribute("disabled", "");
      try {
        const r = await startSignIn(address, age);
        sent(address, age, r.dev?.token ?? null, r.newAccount, r.notices ?? []);
      } catch (x) {
        clear(perr, errorBox(x));
      }
    });
    clear(
      box,
      state(
        "✉",
        "mail",
        "Check your inbox",
        h("p", null, "We sent a link to ", h("b", null, address), ". Open it on this device and you're in. It works once, for 15 minutes."),
        isNew ? h("p", { class: "dim" }, "Opening it creates your account.") : null,
        h("p", { class: "fine" }, "Nothing there? Check spam or promotions; it comes from signin@mail.banger.sh."),
      ),
      notices(list),
      devToken
        ? h(
            "div",
            { class: "note" },
            h("p", null, h("strong", null, "Dev mode: "), "no email is sent; accounts returned the link."),
            h("button", { class: "btn", type: "button", onclick: () => go(devToken) }, "Open the sign-in link"),
          )
        : null,
      h(
        "details",
        { style: { marginTop: "16px" } },
        h("summary", { class: "dim", style: { cursor: "pointer" } }, "Opened the email on another device?"),
        h(
          "form",
          { class: "stack", style: { marginTop: "10px" }, onsubmit: (e: Event) => (e.preventDefault(), go(tokenFromInput(paste.value))) },
          h("label", { for: "paste" }, "Paste the link here"),
          paste,
          h("button", { class: "btn ghost", type: "submit" }, "Sign in"),
        ),
      ),
      perr,
      h("div", { class: "acct-row" }, resend, h("span", { class: "dim" }, "·"), h("button", { class: "acct-link", type: "button", onclick: () => clear(box, form) }, "Use a different email")),
    );
  };

  clear(box, form);
  return stage(box);
}
